/* eslint-disable react-hooks/exhaustive-deps */
import { Iconify } from "@repo/design-system/components/ui";
import PropTypes from 'prop-types';
import { useEffect, useRef } from "react";
import "./TimePicker.scss";
import { TimepickerUI } from "timepicker-ui";
import { sentenceCase } from '@repo/design-system/components/forms/utils';

/**
 * A customizable input component that supports various input types and validation.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.name=""] - The name attribute for the input field.
 * @param {string} [props.placeholder=`${name}`] - The placeholder text for the input field.
 * @param {string|number} props.value - The value of the input field.
 * @param {Function} props.onChange - Callback function when input value changes.
 * @param {Function} [props.onBlur] - Callback function when the input loses focus.
 * @param {string} [props.cssClass="input-field"] - CSS class for styling the input.
 * @param {boolean} [props.isDisable] - Whether the input is disabled.
 * @param {boolean} [props.isReadOnly] - Whether the input is read-only.
 */
const TimePicker = ({
    id, name = "", placeholder,
    value, onChange, onBlur,
    cssClass = "input-field", isdisable, isreadonly,
    iconConfig, mainClass, ...fieldSetting
}) => {

    const inputRef = useRef(null);
    const pickerRef = useRef(null);

    const handleInputChange = (e) => {
        onChange(e);
    };

    const handleKeyDown = (e) => {
        e.preventDefault();
    };

    useEffect(() => {
        if (!inputRef.current) return;

        // Calculate scrollbar width once
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        pickerRef.current = new TimepickerUI(inputRef.current, {
            clock: {
                type: fieldSetting?.clockType ?? "12h",
            },
            ui: {
                editable: true
            },
            labels: {
                ...(fieldSetting?.timePickerLabels?.ok && { ok: fieldSetting?.timePickerLabels.ok }),
                ...(fieldSetting?.timePickerLabels?.cancel && { cancel: fieldSetting?.timePickerLabels.cancel }),
                ...(fieldSetting?.timePickerLabels?.labelTime && { time: fieldSetting?.timePickerLabels.labelTime }),
            },
            callbacks: {
                onOpen: () => {
                    // Compensate for scrollbar disappearing
                    document.body.style.paddingRight = `${scrollbarWidth}px`;
                },
                onClose: () => {
                    // Reset on close
                    document.body.style.paddingRight = "";
                },
                onConfirm: (data) => {
                    const time = `${data.hour}:${data.minutes}${data.type ? ` ${data.type}` : ""}`;
                    handleInputChange(time);
                    pickerRef.current?.close();

                    setTimeout(() => {
                        if (inputRef.current) {
                            inputRef.current.focus();
                            inputRef.current.blur();
                        }
                    }, 100);
                },
            },
        });

        pickerRef.current.create();

        return () => {
            // Cleanup padding on unmount
            document.body.style.paddingRight = "";
            pickerRef.current?.destroy();
        };
    }, []);

    const handleBlur = (e) => {
        onBlur?.(e);
    };


    return (
        <div className={`input-forms ${mainClass ?? ""}`}>
            <input id={id} value={value} name={name}
                type="text" ref={inputRef}
                className={cssClass} placeholder={sentenceCase(placeholder || name)}
                onChange={() => { }} onKeyDown={handleKeyDown} onBlur={handleBlur}
                disabled={isdisable} readOnly={isreadonly} />
            {iconConfig?.icon && (
                <div className="icon-fix">
                    <div className="input-icon right">
                        <Iconify icon={iconConfig?.icon} className="button-icon"
                            {...iconConfig} />
                    </div>
                </div>
            )}
        </div>
    );
};

TimePicker.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    cssClass: PropTypes.string,
    isdisable: PropTypes.bool,
    isreadonly: PropTypes.bool,
    iconConfig: PropTypes.object
};

export default TimePicker;
